import { useEffect, useState } from 'react';
import { Input } from 'antd';

import { MeasurementControl } from '../Input';
import { readEntryValue } from '../../utils/measurements';
import { NO_TEXT_ASSIST } from '../../utils/fieldAssist';

const TEMPLATE_TOKEN_RE = /\{\{/;

/**
 * The value input for a measurement CONDITION or PREFILL: the same control
 * the item form uses (number + unit picker, two numbers for a composite),
 * over a raw value that may be `{ value, unit }`, a bare number (the
 * element's default unit), or a string literal.
 *
 * A string that does not parse — a hand-typed query with a typo, a unit of
 * another measure — is shown AS TYPED in a plain text box so it can be fixed,
 * rather than opening an empty control that would drop it on the first
 * keystroke. Text mode is sticky for the life of this input (the row is
 * rebuilt with a new key whenever its field or the whole query changes), so
 * a literal that becomes readable mid-typing never yanks the caret into a
 * different control. `onChange` receives `{ value, unit }` / null from the
 * control, or the raw text from the text box.
 *
 * A `{{...}}` template token is text the control cannot read but the API
 * can — it substitutes tokens into string values before compiling a
 * condition, and renders them in a prefill — so it sits in the text box
 * without the "unreadable" warning.
 */
export default function MeasurementValueInput({
	raw,
	options,
	name,
	label,
	id,
	'aria-label': ariaLabel,
	className,
	disabled = false,
	size = 'middle',
	cardId,
	onChange,
}) {
	// Parsed the way the API compiler reads a literal: the measure (every one
	// an element that allows several does, KPR-1085), its own units and the
	// unit a bare number means — never the entry allowlist.
	// An element's own units (KPR-1109) are read as the API reads them.
	const entry = readEntryValue(raw, { measure: options?.measure, measures: options?.measures, ownUnits: options?.ownUnits, defaultUnit: options?.defaultUnit });
	const hasText = raw !== undefined && raw !== null && raw !== '';
	const unreadable = hasText && entry === null;
	const [textMode, setTextMode] = useState(unreadable);

	useEffect(() => {
		if (unreadable) setTextMode(true);
	}, [unreadable]);

	if (textMode || unreadable) {
		const text = typeof raw === 'string' ? raw : String(raw ?? '');
		const flagged = unreadable && !TEMPLATE_TOKEN_RE.test(text);
		return (
			<Input
				{...NO_TEXT_ASSIST}
				id={id}
				className={className}
				status={flagged ? 'warning' : undefined}
				size={size}
				disabled={disabled}
				aria-label={ariaLabel}
				value={text}
				placeholder='e.g. 20 lb'
				title={flagged ? 'Not a readable measurement for this element' : undefined}
				onChange={event => onChange(event.target.value)}
			/>
		);
	}

	return (
		<MeasurementControl
			id={id}
			size={size}
			value={entry}
			element={{ name, label: { singular: label || name }, options: options || {} }}
			disabled={disabled}
			aria-label={ariaLabel}
			cardId={cardId}
			onChange={onChange}
		/>
	);
}
